import React from 'react';
import './Terms.css';

const Terms: React.FC = () => (
  <div className="terms-page">
    <div className="terms-content">
      <h1>TERMS OF SERVICE</h1>
      <h2>Heal Terms of Service</h2>
      <p><strong>Last Updated:</strong> 18th May 2026</p>
      <p>
        These Terms of Service (the "Terms") govern the products and services of Heal and its subsidiaries and affiliates ("Heal," "we," or "us"), including our website, mobile applications, wearable integrations, and other online services (collectively, the "Services").
      </p>
      <p>
        By accessing or using the Services, you agree to be bound by these Terms. If you do not agree, you must not use our Services.
      </p>
      
      <h3>Service description</h3>
      <p>
        Heal is a music and breathwork application that uses AI-driven technology to provide personalized sound therapy, music recommendations, and wellness experiences based on your physiological data including HRV, Heart rate and preferences.
      </p>

      <h2>1. Agreement to Terms</h2>
      <p>By using Heal, you confirm that you have read, understood, and agreed to these Terms.</p>

      <h2>2. Privacy Policy & Data Use</h2>
      <p>
        Our Privacy Policy governs how we collect, use, and protect your data, including Apple HealthKit data and wearable integrations.
      </p>
      <p>Please review our Privacy Policy in about section on application.</p>

      <h2>3. Service Use & Eligibility</h2>
      <h3>Who Can Use Heal?</h3>
      <ul>
        <li>You must be at least 18 years old to use Heal.</li>
        <li>If you are under 18 years old, your parent or guardian must agree to these Terms.</li>
      </ul>

      <h2>4. Account Registration</h2>
      <ul>
        <li>You may need to create an account to access certain features.</li>
        <li>You must provide accurate information and maintain account security.</li>
      </ul>
      
      <h3>Third-Party Services & Integrations</h3>
      <ul>
        <li>Heal integrates with Apple Health, Whoop, Oura, Garmin, Google Health and other wearables.</li>
        <li>We do not control third-party services and are not responsible for their availability, accuracy, or failures. Your use of these is subject to their terms.</li>
      </ul>

      <h2>5. Apple Health & Wearable Integrations</h2>
      <h3>Health Data Access & Usage</h3>
      <ul>
        <li>Heal integrates with Apple HealthKit, Apple Watch, and wearables to track HRV, heart rate, stress, and sleep patterns.</li>
        <li>You may choose to grant Heal access to read and/or write health data.</li>
      </ul>
      
      <h3>Health Data Protection & Compliance</h3>
      <ul>
        <li>We do NOT use HealthKit data for advertising, or marketing.</li>
        <li>Health data is used only for wellness insights and recommendations.</li>
        <li>You can revoke access at any time in your Apple Health settings.</li>
      </ul>

      <h2>6. AI-Generated Insights & Limitations</h2>
      <h3>AI-Powered Personalization</h3>
      <p>Heal provides AI-driven music recommendations and sound therapy based on:</p>
      <ul>
        <li>Your physiological data (HRV, heart rate, stress levels, etc.)</li>
        <li>User-reported mood and emotional state</li>
        <li>Music and Listening preferences and engagement patterns</li>
      </ul>
      
      <h3>Limitations of AI Recommendations</h3>
      <ul>
        <li>AI-generated insights are NOT medical advice.</li>
        <li>AI recommendations may vary per individual and should not replace professional medical consultation.</li>
      </ul>

      <h2>7. Medical Disclaimer & Health Risks</h2>
      <h3>Not Medical Advice</h3>
      <ul>
        <li>Heal is not a medical provider and does not offer medical advice, diagnoses, treatment, or emergency care.</li>
        <li>The Services are for informational and wellness purposes only and should not be considered a substitute for professional medical advice.</li>
        <li>Always consult a licensed physician or qualified healthcare professional before making any health, medical, or lifestyle decisions based on information provided by Heal.</li>
      </ul>
      
      <h3>Health Risks & Contraindications</h3>
      <p>By using Heal, you acknowledge and agree that:</p>
      <ul>
        <li>Sound therapy, breath-work, and frequency-based interventions may not be suitable for everyone.</li>
      </ul>
      
      <p><strong>DO NOT use Heal if you:</strong></p>
      <ul>
        <li>Have a history of seizures, epilepsy, glaucoma or photosensitivity</li>
        <li>Have a pacemaker, implanted medical device, or any heart conditions</li>
        <li>Have severe anxiety, PTSD, schizophrenia, bipolar disorder, or other psychiatric conditions</li>
        <li>Are pregnant (avoid extended breath holds or fast paced breathing).</li>
        <li>Have asthma, respiratory issues, chronic illness or high blood pressure</li>
      </ul>
      <p>Always consult your doctor before starting.</p>
      
      <h3>Personal Responsibility & Acknowledgment of Risk</h3>
      <ul>
        <li>By using Heal, you acknowledge and assume all risks associated with sound therapy, breath-work, Visualisation, and relaxation techniques.</li>
        <li>Always practice in a safe environment, Do not use breathwork while driving, in water, operating machinery or at heights.</li>
        <li>If you experience dizziness, nausea, discomfort, anxiety, or any adverse reaction, STOP IMMEDIATELY and consult a healthcare professional.</li>
        <li>Heal is not responsible for injuries, exacerbation of medical conditions, or other adverse effects resulting from your use of the Services.</li>
      </ul>
      
      <h3>Emergency Situations</h3>
      <ul>
        <li>Heal is not an emergency service and does not provide crisis intervention.</li>
        <li>If you are experiencing a medical emergency, call 911 (or local emergency services) immediately.</li>
        <li>If you are in distress or require mental health support, seek professional assistance from a licensed medical provider.</li>
      </ul>

      <h2>8. User-Generated Content (UGC) & Community Guidelines</h2>
      <p>You may submit content (e.g., feedback, mood reports, music preferences). You grant us a worldwide, royalty-free, sub-licensable license to use it for the Services.</p>
      <ul>
        <li>Users must not post:
          <ul>
            <li>False or misleading health claims.</li>
            <li>Hate speech, harassment, or abusive content.</li>
            <li>Advertisements without permission.</li>
          </ul>
        </li>
      </ul>

      <h2>9. Subscription, Payment & Refund Policy</h2>
      <p>
        This section applies to Heal’s paid subscription services (“Paid Subscriptions”).
      </p>

      <h3>9.1 Subscription Plans</h3>
      <p>Heal currently offers the following subscription options (prices in GBP (£)):</p>
      <p><strong>Local Currency &amp; Taxes</strong></p>
      <p>
        “Subscription prices may be displayed in your local currency and may vary by country or region depending on the platform you purchase through (e.g., Apple App Store), applicable taxes, and currency conversion. Where you purchase through a third-party platform, the price and billing terms shown at the point of purchase will apply.”
      </p>
      <p>“Any bank or card-provider fees (including foreign transaction fees) are your responsibility.”</p>
      <ul>
        <li>
          <strong>Annual Subscription</strong>
          <ul>
            <li>£59.88 per year (£4.99/month equivalent)</li>
            <li>Includes a 7-day free trial, after which the full annual fee is charged automatically.</li>
          </ul>
        </li>
        <li>
          <strong>Monthly Subscription</strong>
          <ul>
            <li>£12.99 per month</li>
            <li>Rolling monthly subscription, cancel anytime.</li>
          </ul>
        </li>
        <li>Prices may vary if purchased through third-party platforms (such as the Apple App Store) due to platform policies or taxes.</li>
      </ul>

      <h3>9.2 Free Trial &amp; Automatic Conversion</h3>
      <ul>
        <li>If you start a free trial, you will not be charged during the trial period.</li>
        <li>
          Unless you cancel at least 24 hours before the trial ends, your subscription will automatically convert into a paid subscription, and:
          <ul>
            <li>For annual plans: you will be charged £59.88 for the full year</li>
            <li>For monthly plans: you will be charged £12.99 per month</li>
          </ul>
        </li>
        <li>By starting a free trial, you authorize Heal (or the applicable app store) to charge your selected payment method when the trial ends.</li>
      </ul>

      <h3>9.3 Auto-Renewal</h3>
      <ul>
        <li>All Heal subscriptions are auto-renewing.</li>
        <li>Annual subscriptions renew yearly</li>
        <li>Monthly subscriptions renew monthly</li>
        <li>Your subscription will continue until you cancel it. You authorize us (or the relevant app store) to charge your payment method at the start of each renewal period.</li>
      </ul>

      <h3>9.4 Cancellation</h3>
      <ul>
        <li>You may cancel your subscription at any time.</li>
        <li>
          If purchased via the Apple App Store:
          <ul>
            <li>You must cancel through your Apple ID subscription settings.</li>
          </ul>
        </li>
        <li>
          If purchased directly via Heal:
          <ul>
            <li>You may cancel by emailing help@healmusicapp.com or via your account settings (if available).</li>
          </ul>
        </li>
        <li>Cancellations must be completed at least 24 hours before the renewal date to avoid being charged for the next period.</li>
        <li>
          After cancellation:
          <ul>
            <li>You will retain access to Heal until the end of your current billing period</li>
            <li>No partial refunds are provided for unused time</li>
          </ul>
        </li>
      </ul>

      <h3>9.5 Refunds</h3>
      <ul>
        <li>
          Refunds are handled according to the platform used to purchase your subscription:
          <ul>
            <li>
              Apple App Store purchases:
              <ul>
                <li>Refunds are subject to Apple’s refund policies and must be requested directly through Apple.</li>
              </ul>
            </li>
            <li>
              Direct Heal purchases (if applicable):
              <ul>
                <li>Refund requests may be reviewed on a case-by-case basis and are not guaranteed, except where required by law.</li>
              </ul>
            </li>
          </ul>
        </li>
      </ul>

      <h3>9.6 Price Changes</h3>
      <ul>
        <li>We may change subscription prices from time to time.</li>
        <li>Any price change will apply only at the next renewal</li>
        <li>We will provide reasonable advance notice where required by law</li>
        <li>If you do not agree to a price change, you may cancel your subscription before renewal</li>
      </ul>

      <h3>9.7 Consumer Withdrawal Rights (UK &amp; EEA)</h3>
      <ul>
        <li>
          If you are a UK or EEA consumer, you have a legal right to withdraw within 14 days of purchase and receive a refund unless:
          <ul>
            <li>You have started using the digital services during that period, and</li>
            <li>You acknowledged that your right to withdraw would be lost upon use</li>
          </ul>
        </li>
        <li>This does not affect your statutory rights.</li>
      </ul>

      <h2>10. Data Retention & Deletion</h2>
      <ul>
        <li>Data is retained while your account is active.</li>
        <li>Deleted accounts will have their data removed within 90 days unless required by law.</li>
      </ul>
      <p>To request data deletion, contact Heal customer service team</p>

      <h2>11. Prohibited Conduct</h2>
      <p>You may not:</p>
      <ul>
        <li>Use the Services illegally or to harm others.</li>
        <li>Reverse engineer, bypass security, or extract data.</li>
        <li>Share accounts without consent.</li>
        <li>Interfere with functionality (e.g., viruses, spam).</li>
      </ul>
      <p>We may suspend/terminate access for violations.</p>

      <h2>12. Indemnity & Third-Party Liabilities</h2>
      <ul>
        <li>Heal is not responsible for failures, inaccuracies, or harm caused by third-party integrations (e.g., Apple Health, any wearables, Spotify).</li>
        <li>You agree to indemnify Heal against any claims resulting from your use or misuse of the Services.</li>
      </ul>

      <h2>13. Warranty & Liability</h2>
      <h3>Warranty</h3>
      <p>Services are provided "as is." We disclaim all implied warranties (e.g., fitness for purpose) unless required by law.</p>
      
      <h3>Liability</h3>
      <p>We're not liable for indirect or consequential damages. Our total liability is limited to the greater of $100 or fees paid in the last 12 months, except where law prohibits.</p>

      <h2>14. Termination</h2>
      <p>We may suspend/terminate your access for breaches, legal requirements, or risk, with notice unless prohibited.</p>
      <p>You may terminate by emailing help@healmusicapp.com. Surviving terms (e.g., liability) persist.</p>

      <h2>15. Dispute Resolution & Arbitration</h2>
      <p>Disputes are resolved via binding arbitration under AAA Consumer Rules, not courts, unless you opt out within 30 days by emailing help@healmusicapp.com with your name and intent.</p>
      <p>No class actions allowed.</p>
      <p>Arbitration occurs in England, with fees split per AAA rules unless frivolous.</p>

      <h2>16. Apple-Specific Terms</h2>
      <p>Apple isn't responsible for Heal. We provide support and warranties. Your license is non-transferable, limited to Apple devices you own/control.</p>

      <h2>17. Governing Law & International Use</h2>
      <p>These Terms are governed by the laws of England and Wales.</p>
      <p>International users must comply with local laws; we don't guarantee global availability.</p>

      <h2>18. Changes to Terms or Services</h2>
      <p>We may update Terms with notice (email suffices). Continued use is acceptance.</p>
      <p>Services may be modified/discontinued with reasonable notice unless urgent (e.g., safety).</p>

      <h2>19. Feedback</h2>
      <p>Feedback you provide isn't confidential; we may use it freely without compensation.</p>

      <h2>20. Trademark Acknowledgments</h2>
      <p>
        &quot;Oura&quot; is a trademark of Oura Health Oy. &quot;WHOOP&quot; is a trademark of WHOOP, Inc. &quot;Apple Health&quot; and
        &quot;HealthKit&quot; are trademarks of Apple Inc. Heal is not affiliated with or endorsed by these providers.
      </p>

      <h2>21. Contact Us</h2>
      <p>For questions or support, contact:</p>
      <p>Email: <a href="mailto:help@healmusicapp.com">help@healmusicapp.com</a></p>
    </div>
  </div>
);

export default Terms;